import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MenuModal    from '../../navigation/ui/Main';
import UserModal    from '../../modals/user_modal/ui/Main';
import SearchModal  from '../../modals/search_modal/ui/Main';
import LanguageModal from '../../modals/language_modal/ui/Main';

import logoIcon   from '../../../assets/icons/logo.svg';
import barIcon    from '../../../assets/icons/bar.svg';
import searchIcon from '../../../assets/icons/search.svg';
import globIcon   from '../../../assets/icons/glob.svg';
import userIcon   from '../../../assets/icons/user.svg';
import addIcon    from '../../../assets/icons/add.svg';
import closeIcon  from '../../../assets/icons/close.svg';

import { getCountries } from '../../../api/location/getCountries';
import { getRegions }   from '../../../api/location/getRegions';
import { getLocalizedName } from '../../../api/location/locationUtils';

import '../../../language/i18n';

const WHITE_FILTER = '[filter:brightness(0)_invert(1)]';
const GREEN_FILTER = '[filter:brightness(0)_saturate(100%)_invert(18%)_sepia(70%)_saturate(6415%)_hue-rotate(142deg)_brightness(93%)_contrast(84%)]';
const GREY_FILTER  = '[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(0%)_saturate(0%)_brightness(90%)]';

/* Reusable icon button */
function IconBtn({ src, alt, onClick, active, green, white, size = 20, badge }) {
    return (
        <button
            onClick={onClick}
            className={[
                'relative flex items-center justify-center rounded-full border-none cursor-pointer transition-all duration-150 shrink-0',
                green
                    ? 'bg-[#147F26] hover:bg-[#7AB30E] w-10 h-10'
                    : active
                        ? 'bg-[#e8f5ea] w-10 h-10'
                        : 'bg-transparent hover:bg-[#F9F9F9] w-10 h-10',
            ].join(' ')}
        >
            <img
                src={src}
                alt={alt}
                width={size}
                height={size}
                className={`transition-all duration-150 ${
                    green || white ? WHITE_FILTER : active ? GREEN_FILTER : GREY_FILTER
                }`}
            />
            {badge && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 border-2 border-white" />
            )}
        </button>
    );
}

function Header({ onToggleMenu = () => {} }) {
    const [menus, setMenus] = useState({
        menu: false, user: false, search: false, language: false,
    });

    const { t, i18n } = useTranslation();
    const navigate    = useNavigate();

    const [currentLanguage, setCurrentLanguage] = useState(
        localStorage.getItem('language') || 'kz'
    );

    useEffect(() => { i18n.changeLanguage(currentLanguage); }, [currentLanguage, i18n]);

    useEffect(() => {
        async function fetchLocations() {
            try {
                const requiredCountryNames = {
                    kz: ['Қазақстан', 'Өзбекстан', 'Қытай', 'Қырғызстан'],
                    ru: ['Казахстан', 'Узбекистан', 'Китай', 'Кыргызстан'],
                    en: ['Kazakhstan', 'Uzbekistan', 'China', 'Kyrgyzstan'],
                    cn: ['哈萨克斯坦', '乌兹别克斯坦', '中国', '吉尔吉斯斯坦'],
                };
                const langKey = ['kz','ru','en','cn'].includes(currentLanguage) ? currentLanguage : 'ru';
                const requiredNames = requiredCountryNames[langKey];

                const [countriesData, regionsData] = await Promise.all([getCountries(), getRegions()]);
                const existingNames = countriesData.map(c => getLocalizedName(c, currentLanguage));
                const missing = requiredNames
                    .map((name, i) => !existingNames.includes(name) && {
                        id: 1000 + i,
                        name_kz: requiredCountryNames.kz[i],
                        name_ru: requiredCountryNames.ru[i],
                        name_en: requiredCountryNames.en[i],
                        name_zh: requiredCountryNames.cn[i],
                        name_cn: requiredCountryNames.cn[i],
                    })
                    .filter(Boolean);

                void [...countriesData, ...missing];
                void regionsData;
            } catch (e) {
                console.error('Error fetching locations:', e);
            }
        }
        fetchLocations();
    }, [currentLanguage]);

    const changeLanguage = (lng) => {
        setCurrentLanguage(lng);
        localStorage.setItem('language', lng);
        i18n.changeLanguage(lng);
    };

    const toggle = useCallback((name) => {
        if (name === 'user' && !localStorage.getItem('access_token')) {
            navigate('/sign');
            return;
        }
        setMenus(prev => {
            const next = { ...prev, [name]: !prev[name] };
            if (name === 'menu') onToggleMenu(next.menu);
            return next;
        });
    }, [navigate, onToggleMenu]);

    return (
        <>
            <header className="
                sticky top-0 z-[90] w-full
                bg-white border-b border-[#EDEDED]
            ">
                <div className="
                    flex items-center justify-between
                    h-[64px] px-4
                    md:px-[60px] lg:px-[120px] xl:px-[200px]
                ">

                    {/* ── Left: burger (desktop) + logo ─────────────── */}
                    <div className="flex items-center gap-3">
                        {/* Burger — desktop only */}
                        <button
                            onClick={() => toggle('menu')}
                            className="
                                hidden md:flex items-center justify-center
                                w-10 h-10 rounded-xl border border-[#EDEDED]
                                bg-white hover:bg-[#F9F9F9]
                                cursor-pointer transition-colors duration-150
                            "
                        >
                            <img
                                src={menus.menu ? closeIcon : barIcon}
                                alt="menu"
                                width={20}
                                height={20}
                                className={GREY_FILTER}
                            />
                        </button>

                        {/* Logo */}
                        <Link to="/" className="flex items-center shrink-0">
                            <img
                                src={logoIcon}
                                alt="Agroland"
                                className={`h-7 ${GREEN_FILTER}`}
                            />
                        </Link>
                    </div>

                    {/* ── Right: actions ────────────────────────────── */}
                    <div className="flex items-center gap-1.5 md:gap-2">

                        {/* Language */}
                        <div className="relative">
                            <IconBtn
                                src={globIcon}
                                alt="language"
                                onClick={() => toggle('language')}
                                active={menus.language}
                            />
                            <LanguageModal
                                menuVisible={menus.language}
                                toggleMenu={() => toggle('language')}
                                onSelectLanguage={changeLanguage}
                            />
                        </div>

                        {/* Search */}
                        <IconBtn
                            src={searchIcon}
                            alt="search"
                            onClick={() => toggle('search')}
                            active={menus.search}
                        />

                        {/* Create ad — desktop only */}
                        <button
                            onClick={() => navigate('/adform')}
                            className="
                                hidden md:flex items-center gap-2
                                bg-[#147F26] hover:bg-[#7AB30E]
                                text-white text-[14px] font-semibold
                                px-4 h-10 rounded-xl border-none cursor-pointer
                                transition-colors duration-150 whitespace-nowrap
                            "
                        >
                            <img src={addIcon} alt="" width={16} height={16} className={WHITE_FILTER} />
                            {t('createAnnouncement')}
                        </button>

                        {/* User avatar — desktop only */}
                        <div className="relative hidden md:block">
                            <IconBtn
                                src={userIcon}
                                alt="user"
                                onClick={() => toggle('user')}
                                green
                            />
                            <UserModal
                                menuVisible={menus.user}
                                toggleMenu={() => toggle('user')}
                            />
                        </div>

                        {/* Mobile burger */}
                        <button
                            onClick={() => toggle('menu')}
                            className="
                                md:hidden flex items-center justify-center
                                w-10 h-10 rounded-xl
                                bg-[#147F26] hover:bg-[#7AB30E]
                                border-none cursor-pointer transition-colors duration-150
                            "
                        >
                            <img
                                src={menus.menu ? closeIcon : barIcon}
                                alt="menu"
                                width={20}
                                height={20}
                                className={WHITE_FILTER}
                            />
                        </button>
                    </div>
                </div>
            </header>

            {/* Modals — outside header flow */}
            <MenuModal   menuVisible={menus.menu}   toggleMenu={() => toggle('menu')} />
            <SearchModal menuVisible={menus.search} toggleMenu={() => toggle('search')} />
        </>
    );
}

export default Header;
